import { useAppDispatch, useAppSelector } from "app/hooks";
import { CenteredDashboardContent } from "features/dashboard/DashboardContent";
import { GenericLoading } from "components/ui/Loader";
import { useEffect, useState } from "react";
import { PricingModeEnum } from "api/pricing";
import GenericTabs from "components/navigation/Tabs/Tabs";
import { NsKey } from "enums/NsKey";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { useParams } from "react-router-dom";
import { FunnelCard } from "./components/funnel-card/FunnelCard";
import { FormulasMessage } from "./components/formulas/FormulasMessage";
import { PricingHeader } from "./components/PricingHeader";
import { PricingSideMenu } from "./components/PricingSideMenu";
import { FunnelCardSkeletonList } from "./components/loading/FunnelCardSkeleton";
import { PricingHeaderSkeleton } from "./components/loading/PricingHeaderSkeleton";
import { PricingSideMenuSkeleton } from "./components/loading/PricingSideMenuSkeleton";
import { ShoppingCart } from "./components/shopping-cart/ShoppingCart";
import { ParcGlobalGuaranteesHydrator } from "./components/parc/ParcGlobalGuaranteesHydrator";
import { PricingParcProvider } from "./contexts/PricingParcContext";
import { clearGuaranteeLastResearchHash } from "./slices/pricing/pricingSlice";
import { selectFilteredComparableFunnels, selectFilteredContractTunnels, selectFilteredFormulaFunnels, selectFormulaProductLabels, selectIsCartAvailable, selectIsPricingBusy, selectPricingMode, selectPricingPageLoading, selectQuoteCreationLoading } from "./slices/pricing/selectors";
import { ContractFunnel } from "./types/interfaces";
import { fetchFunnels } from "./slices/pricing/thunks/fetchFunnelsThunk";

export const PricingProcess: React.FC = () => {
  const { riskId } = useParams<{ riskId: string }>();
  const dispatch = useAppDispatch()
  const loading = useAppSelector(selectQuoteCreationLoading)

  // Redux state
  const contractFunnels = useAppSelector(selectFilteredContractTunnels);

  const isCartAvailable = useAppSelector(selectIsCartAvailable);
  // Re-tarif global en cours : overlay sur les cartouches + actions (panier/devis) bloquées.
  const isPricingBusy = useAppSelector(selectIsPricingBusy);
  const isPricingPageLoading = useAppSelector(selectPricingPageLoading);

  // Comparable products only (classic), fixed formulas only, or both: set by the comparator response.
  const mode = useAppSelector(selectPricingMode);
  const comparableFunnels = useAppSelector(selectFilteredComparableFunnels);
  const formulaFunnels = useAppSelector(selectFilteredFormulaFunnels);
  const formulaProductLabels = useAppSelector(selectFormulaProductLabels);
  const { t } = useDynamicTranslation([NsKey.Pricing]);

  // Local state
  const [isProMode, setIsProMode] = useState<boolean>(false);

  useEffect(() => {
    if (riskId) {
      dispatch(clearGuaranteeLastResearchHash())
      dispatch(fetchFunnels(riskId))
    }
  }, [dispatch, riskId])

  if (!riskId) {
    return <></>
  }

  const renderCards = (funnels: ContractFunnel[]) => funnels.map((item, index) => (
    <FunnelCard
      key={index}
      index={index}
      isProMode={isProMode}
      contractFunnelId={item.id}
      contractIdentifier={item.contractIdentifier}
    />
  ));

  // Page load: skeletons of the header, the side menu and the cards, the same as the borrower
  // pricing page. On repricing (cards present), each card's price carries its own loader.
  // The header and the side menu only give way on the page load: a repricing with no card left
  // (all filtered out) must not unmount the filters or the fees being edited.
  const areCardsLoading = isPricingPageLoading || (isPricingBusy && contractFunnels.length === 0);

  // Results: the cards of the comparable products (classic page and "Products" tab), those of
  // the formulas (formulas page and "Formulas" tab), or both as tabs.
  const renderResults = () => {
    if (areCardsLoading) {
      return <FunnelCardSkeletonList count={3} showProMode={isProMode} />;
    }
    if (mode === PricingModeEnum.Formulas) {
      return renderCards(formulaFunnels);
    }
    if (mode === PricingModeEnum.Mixed) {
      return (
        <GenericTabs defaultTab="products">
          <GenericTabs.Item label={t("PRICING.TABS.PRODUCTS")} value="products" />
          <GenericTabs.Content value="products">
            <div className="flex flex-col gap-4">{renderCards(comparableFunnels)}</div>
          </GenericTabs.Content>
          <GenericTabs.Item label={t("PRICING.TABS.FORMULAS")} value="formulas" />
          <GenericTabs.Content value="formulas">
            <div className="flex flex-col gap-4">
              <FormulasMessage productLabels={formulaProductLabels} />
              {renderCards(formulaFunnels)}
            </div>
          </GenericTabs.Content>
        </GenericTabs>
      );
    }
    return renderCards(contractFunnels);
  };

  const solutionsContent = (
    <div className="flex flex-col xl:flex-row gap-6">
      {isPricingPageLoading
        ? <PricingSideMenuSkeleton showProMode={isProMode} />
        : <PricingSideMenu setIsProMode={setIsProMode} isProMode={isProMode} />}
      <div className="relative flex flex-col ml-4 gap-4 w-full">
        <p className="text-2xl font-bold">Résultats de recherche</p>

        {renderResults()}

        {/* Shopping Cart */}
        {isCartAvailable && (
          <ShoppingCart riskId={riskId} />
        )}
      </div>
    </div>
  );

  return (
    <CenteredDashboardContent>
      <PricingParcProvider riskId={riskId}>
        <ParcGlobalGuaranteesHydrator />
        <div className="flex flex-col gap-6">
          {
            loading &&
            <GenericLoading loading isFullPage />
          }
          {/* Formulas only: the message replaces the guarantees block, to the right of the person. */}
          {isPricingPageLoading
            ? <PricingHeaderSkeleton />
            : <PricingHeader
                rightBlock={mode === PricingModeEnum.Formulas ? <FormulasMessage productLabels={formulaProductLabels} /> : undefined}
              />}

          {solutionsContent}
        </div>
      </PricingParcProvider>
    </CenteredDashboardContent>
  );
};
